import React, { useRef, useCallback, useMemo } from 'react';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import saveAs from 'file-saver';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { useStacklineTheme } from '@stackline/ui';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import AtlasV2ComparableSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ComparableSplineChart';

enum ContentScoreField {
  Title = 'titleScore',
  Bullets = 'bulletScore',
  Image = 'imageScore',
  Video = 'videoScore',
  APlus = 'aplusScore'
}

/**
 * Displays visualizations for the content score page
 */
const AtlasV2ContentTopSection = () => {
  const { endWeek } = useAppSelector((state) => state.mainTimePeriod);

  const formatMetric = useMetricFormatter();
  const theme = useStacklineTheme();

  const legendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const donutChartRef = useRef<HTMLDivElement>(null);

  const { getValuesByFieldName, isLoading } = useAtlasV2TableData({
    fields: [
      {
        name: 'contentScore'
      },
      {
        name: ContentScoreField.APlus
      },
      {
        name: ContentScoreField.Bullets
      },
      {
        name: ContentScoreField.Image
      },
      {
        name: ContentScoreField.Title
      },
      {
        name: ContentScoreField.Video
      }
    ],
    groupByFieldName: 'retailerId',
    indexName: 'sales',
    sortFieldName: 'contentScore',
    itemsPerPage: 1200,
    pageNumber: 1,
    requestId: 'content-score-donut',
    startWeekId: endWeek
  });
  const { getValuesByFieldName: getEachScoreFieldName, isLoading: isTopEntityLoading } = useAtlasV2TableData({
    fields: [
      {
        name: ContentScoreField.APlus
      },
      {
        name: ContentScoreField.Bullets
      },
      {
        name: ContentScoreField.Image
      },
      {
        name: ContentScoreField.Title
      },
      {
        name: ContentScoreField.Video
      }
    ],
    groupByFieldName: 'retailerId',
    indexName: 'sales',
    sortFieldName: 'titleScore',
    itemsPerPage: 1200,
    pageNumber: 1,
    requestId: 'content-score-topEntity'
  });

  const getContentValue = useCallback(
    (fieldName: string): number => getValuesByFieldName(fieldName)[0] || 0,
    [getValuesByFieldName]
  );

  const eachContentValue = useCallback(
    (fieldName: string): number => getEachScoreFieldName(fieldName)[0] || 0,
    [getEachScoreFieldName]
  );

  const contentScore = useMemo(() => getContentValue('contentScore'), [getContentValue]);

  const contentScores = [
    ContentScoreField.Title,
    ContentScoreField.Bullets,
    ContentScoreField.Image,
    ContentScoreField.Video,
    ContentScoreField.APlus
  ].reduce(
    (acc, fieldName) => ({
      ...acc,
      [fieldName]: {
        value: eachContentValue(fieldName)
      }
    }),
    {}
  );

  const data = [
    ['Title Score', contentScores[ContentScoreField.Title].value],
    ['Bullets Score', contentScores[ContentScoreField.Bullets].value],
    ['Image Score', contentScores[ContentScoreField.Image].value],
    ['Video Score', contentScores[ContentScoreField.Video].value],
    ['Enhanced Score', contentScores[ContentScoreField.APlus].value]
  ];

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <div ref={donutChartRef} style={{ width: '100%', height: '100%' }}>
          <Flex flexDirection="column" gap="xxl">
            <CommonSummaryInfoSubtitle title="Content Utilization" />
            <Flex alignItems="center" gap={88}>
              <div style={{ width: '308px', height: '308px' }}>
                {isLoading || isTopEntityLoading ? (
                  <DonutChartLoading />
                ) : (
                  <CircularProgressPercentage percentage={contentScore} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                {isLoading ? (
                  <SplineChartLoading width={543} />
                ) : (
                  <BeaconChartWithLegend
                    showTitleSummary={false}
                    convertSeriesToDelimitedData={() => {
                      const csvContent = data.map((rowElements) => rowElements.join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/plain;charset=utf-8' });
                      saveAs(blob, `Content Score.csv`);
                      return Promise.resolve();
                    }}
                    primaryLegendProps={{
                      displayName: legendLabel
                    }}
                    title="Content Score"
                    saveImageRef={donutChartRef}
                  >
                    <TopEntityChartV2
                      chartSeries={[
                        {
                          type: 'column',
                          data,
                          color: theme.colors.primary
                        }
                      ]}
                      seriesColors={[theme.colors.primary]}
                      chartOptionsOverride={(defaults) => ({
                        ...defaults,
                        plotOptions: {
                          ...defaults.plotOptions,
                          series: {
                            ...defaults?.series?.plotOptions,
                            dataLabels: {
                              ...defaults.plotOptions?.series?.dataLabels,
                              formatter() {
                                return ` ${formatMetric(this.y, METRICTYPE.PERCENT, {
                                  decimalPlaces: 1,
                                  showFullValue: false
                                })}`;
                              }
                            }
                          }
                        },
                        chart: {
                          ...defaults.chart,
                          height: 240,
                          spacingTop: 15,
                          marginBottom: 39,
                          spacingBottom: 0
                        }
                      })}
                      leftAlignBars={false}
                    />
                  </BeaconChartWithLegend>
                )}
              </div>
            </Flex>
          </Flex>
        </div>
        <AtlasV2ComparableSplineChart title="Content Utilization" indexName="sales" fieldName="contentScore" />
      </Flex>
    </BasePageContainer>
  );
};

export default AtlasV2ContentTopSection;
